import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Stethoscope } from "lucide-react";

export default function NurseAuth() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.role === "nurse") navigate("/app", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        if (u.role === "admin") { navigate("/admin"); return; }
      } else {
        await register(form.name, form.email, form.password);
      }
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#fcf9f8] flex flex-col">
      <div className="max-w-md mx-auto w-full flex-1 flex flex-col justify-center px-6 py-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-lg bg-[#003d9b] flex items-center justify-center">
            <Stethoscope className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-[22px] font-bold text-[#1c1b1b] leading-tight">MITRA Training</h1>
            <p className="text-sm text-[#434654]">Nurse pre-onboarding portal</p>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
          <div className="flex gap-2 mb-6 bg-[#f3f4f6] p-1 rounded-lg">
            {["login", "signup"].map((m) => (
              <button
                key={m}
                data-testid={`auth-tab-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 h-10 rounded-md text-sm font-semibold transition-colors ${
                  mode === m ? "bg-white text-[#003d9b] shadow-sm" : "text-[#434654]"
                }`}
              >
                {m === "login" ? "Log In" : "Sign Up"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <div>
                <label className="text-sm font-semibold text-[#1c1b1b]">Full name</label>
                <input
                  data-testid="auth-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 h-12 w-full rounded-lg border border-[#E5E7EB] bg-white px-4 text-[16px] focus:outline-none focus:ring-2 focus:ring-[#003d9b]"
                  placeholder="Jane Doe"
                />
              </div>
            )}
            <div>
              <label className="text-sm font-semibold text-[#1c1b1b]">Email</label>
              <input
                data-testid="auth-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 h-12 w-full rounded-lg border border-[#E5E7EB] bg-white px-4 text-[16px] focus:outline-none focus:ring-2 focus:ring-[#003d9b]"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-[#1c1b1b]">Password</label>
              <input
                data-testid="auth-password-input"
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1 h-12 w-full rounded-lg border border-[#E5E7EB] bg-white px-4 text-[16px] focus:outline-none focus:ring-2 focus:ring-[#003d9b]"
                placeholder="••••••••"
              />
            </div>
            <button
              data-testid="auth-submit-btn"
              disabled={loading}
              className="w-full h-12 rounded-lg bg-[#003d9b] text-white text-[16px] font-semibold hover:bg-[#0052cc] transition-colors disabled:opacity-50"
            >
              {loading ? "Please wait…" : mode === "login" ? "Log In" : "Create Account"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[#E5E7EB]" />
            <span className="text-xs text-[#9CA3AF] uppercase tracking-wider font-semibold">or</span>
            <div className="flex-1 h-px bg-[#E5E7EB]" />
          </div>

          <button
            data-testid="auth-google-btn"
            onClick={googleLogin}
            className="w-full h-12 rounded-lg bg-white border border-[#E5E7EB] text-[#1c1b1b] text-[15px] font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
            Continue with Google
          </button>
        </div>

        <button
          data-testid="admin-portal-link"
          onClick={() => navigate("/admin/login")}
          className="mt-6 text-sm text-[#434654] hover:text-[#003d9b] transition-colors mx-auto"
        >
          MITRA Admin Portal →
        </button>
      </div>
    </div>
  );
}
